import { ref, computed, onMounted, onUnmounted } from "vue"
import { apiService } from "@/services/api"

export type ConnectionStatus = "online" | "unstable" | "offline"

// Singleton state - shared across all instances
// eslint-disable-next-line no-undef
const isOnline = ref(navigator.onLine)
// eslint-disable-next-line no-undef
const isOffline = ref(!navigator.onLine)
// eslint-disable-next-line no-undef
const connectionStatus = ref<ConnectionStatus>(navigator.onLine ? "online" : "offline")

// Track recent health check results for stability detection
const recentHealthChecks = ref<Array<{ success: boolean; timestamp: number }>>([])
const healthCheckInterval = ref<ReturnType<typeof setInterval> | null>(null)
let isInitialized = false

export const useNetworkStatus = () => {

  const updateStatus = () => {
    // eslint-disable-next-line no-undef
    isOnline.value = navigator.onLine
    // eslint-disable-next-line no-undef
    isOffline.value = !navigator.onLine
    
    // If navigator says offline, immediately mark as offline
    // eslint-disable-next-line no-undef
    if (!navigator.onLine) {
      connectionStatus.value = "offline"
    }
    
    // eslint-disable-next-line no-console
    console.log("🌐 [Network] Status changed:", {
      isOnline: isOnline.value,
      isOffline: isOffline.value,
      connectionStatus: connectionStatus.value,
      timestamp: new Date().toISOString(),
    })
  }

  const checkHealthEndpoint = async (): Promise<{ success: boolean; responseTime: number }> => {
    const startTime = Date.now()
    try {
      const response = await apiService.get<{ status: string }>("/health")
      const responseTime = Date.now() - startTime
      // Consider response slow if > 1000ms (1 second)
      const isSlow = responseTime > 1000
      
      return {
        success: response.status === "ok" && !isSlow,
        responseTime,
      }
    } catch (error) {
      const responseTime = Date.now() - startTime
      // eslint-disable-next-line no-console
      console.warn("⚠️ [Network] Health check failed:", error)
      return {
        success: false,
        responseTime,
      }
    }
  }

  const updateConnectionStatus = async () => {
    // Don't check if navigator says offline
    // eslint-disable-next-line no-undef
    if (!navigator.onLine) {
      connectionStatus.value = "offline"
      return
    }

    const healthResult = await checkHealthEndpoint()
    const now = Date.now()
    
    // Keep only last 5 health checks (last ~50 seconds if checking every 10s)
    recentHealthChecks.value.push({
      success: healthResult.success,
      timestamp: now,
    })
    
    // Keep only checks from last 60 seconds
    recentHealthChecks.value = recentHealthChecks.value.filter(
      (check) => now - check.timestamp < 60000
    )

    // Determine status based on recent checks
    if (recentHealthChecks.value.length === 0) {
      connectionStatus.value = "offline"
      return
    }

    const successCount = recentHealthChecks.value.filter((check) => check.success).length
    const successRate = successCount / recentHealthChecks.value.length

    // If less than 50% success rate or slow response, mark as unstable
    if (successRate < 0.5 || healthResult.responseTime > 1000) {
      connectionStatus.value = "unstable"
    } else if (successRate >= 0.8) {
      // If 80%+ success rate and fast, mark as online
      connectionStatus.value = "online"
    } else {
      // Between 50-80% = unstable
      connectionStatus.value = "unstable"
    }

    // eslint-disable-next-line no-console
    console.log("🌐 [Network] Connection status updated:", {
      status: connectionStatus.value,
      successRate: `${(successRate * 100).toFixed(0)}%`,
      recentChecks: recentHealthChecks.value.length,
      lastResponseTime: healthResult.responseTime,
    })
  }

  const startHealthCheckInterval = () => {
    // Clear existing interval if any
    if (healthCheckInterval.value) {
      clearInterval(healthCheckInterval.value)
    }

    // Initial check
    updateConnectionStatus()

    // Check every 10 seconds
    healthCheckInterval.value = setInterval(() => {
      updateConnectionStatus()
    }, 10000)
  }

  const stopHealthCheckInterval = () => {
    if (healthCheckInterval.value) {
      clearInterval(healthCheckInterval.value)
      healthCheckInterval.value = null
    }
  }

  const checkConnection = async (): Promise<boolean> => {
    try {
      // Try to fetch a small resource to verify actual connectivity
      // eslint-disable-next-line no-undef
      const response = await fetch("/favicon.ico", {
        method: "HEAD",
        cache: "no-cache",
        // eslint-disable-next-line no-undef
        signal: AbortSignal.timeout(5000), // 5 second timeout
      })
      return response.ok
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("⚠️ [Network] Connection check failed:", error)
      return false
    }
  }

  onMounted(() => {
    // Initialize only once (singleton pattern)
    if (isInitialized) {
      return
    }

    isInitialized = true

    // Set initial status
    updateStatus()

    // Listen to online/offline events
    if (typeof window !== "undefined") {
      // eslint-disable-next-line no-undef
      window.addEventListener("online", () => {
        updateStatus()
        startHealthCheckInterval()
      })
      // eslint-disable-next-line no-undef
      window.addEventListener("offline", () => {
        updateStatus()
        stopHealthCheckInterval()
      })
    }

    // Start periodic health checks if online
    if (typeof navigator !== "undefined") {
      // eslint-disable-next-line no-undef
      if (navigator.onLine) {
        startHealthCheckInterval()
      }
    }

    // eslint-disable-next-line no-console
    console.log("🌐 [Network] Network status listener initialized")
  })

  onUnmounted(() => {
    // Don't remove listeners on unmount - they're shared
    // Only log for debugging
    // eslint-disable-next-line no-console
    console.log("🌐 [Network] Component unmounted (listeners remain active)")
  })

  return {
    isOnline,
    isOffline,
    connectionStatus: computed(() => connectionStatus.value),
    checkConnection,
  }
}
